import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { MessageSignature } from '../components/admin/MessageSignature';
import { EvaSignature, EvaResolution } from '../components/Eva';
import MessageAttachment from '../components/MessageAttachment';
import RichMessageBody, { hasRichBody } from '../components/RichMessageBody';
import RichComposer from '../components/RichComposer';
import { isUploadChecking, heldUploadMessage } from '../constants/uploadChecking';
import { friendlyFileName } from '../constants/borrowerDocumentName';
// In the guided shell the frame passes on the height the shell gives it, so the page can fill it (the box stays pinned).
const BareFrame = ({ children }) => <div data-testid="bare-frame" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</div>;

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString('en-US', { weekday: 'short' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtFull(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ── New Conversation Modal ────────────────────────────────────────────────────
const SUBJECT_CHIPS = [
  'Document Upload Help',
  'Loan Status Question',
  'Rate & Terms',
  'Closing Timeline',
  'Other',
];

function NewConvModal({ token, onCreated, onClose }) {
  const [subject, setSubject] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  async function handleCreate() {
    const s = subject.trim();
    if (!s) { setErr('Please enter or select a subject.'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/messages/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject: s }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to create conversation.');
      onCreated(d.conversation);
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  }

  return (
    <div className="safe-modal-overlay fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">New message</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">What's this about?</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {SUBJECT_CHIPS.map(c => (
                <button
                  key={c}
                  onClick={() => setSubject(c)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    subject === c
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Or type a custom subject..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {err && <p className="text-red-600 text-sm">{err}</p>}
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button onClick={handleCreate} disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Starting...' : 'Start Conversation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Messages Page ────────────────────────────────────────────────────────
// 🔑 bare (Jesse, 2026-10-19): the same page without the dashboard app shell, for the guided shell's in-shell view
// (pages/GuidedFlowPage). Everything else — the data, the actions — is this page unchanged.
export default function Messages({ bare = false } = {}) {
  const Frame = bare ? BareFrame : AppShell;
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [sending, setSending] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [mobileShowThread, setMobileShowThread] = useState(false);
  // 🔑 A MESSAGE NOT YET IN A CONVERSATION (Jesse, 2026-10-19): opening the page creates nothing; the conversation is made
  // with the first message sent. `draft` is the composer with no conversation behind it yet.
  const [draft, setDraft] = useState(false);
  // Optimistic overlay for resolution clicks, keyed by Eva message id, so the
  // buttons resolve immediately rather than waiting on the 30s poll.
  const [resolutions, setResolutions] = useState({});
  const [resolvingId, setResolvingId] = useState(null);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  // 🔑 EMPTY CONVERSATIONS ARE NOT SHOWN to the borrower (2026-10-19): the server used to make one on every visit. The
  // newest empty one, if any, is reused for the next message instead of starting another.
  const hasMessages = (c) => c.message_count == null || Number(c.message_count) > 0;
  const visible = conversations.filter(hasMessages);
  const emptyConv = conversations.find((c) => !hasMessages(c) && c.status === 'open') || null;

  const fetchConversations = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok) setConversations(d.conversations || []);
      if (r.ok) setLoaded(true);
    } catch (_) {}
  }, [token]);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const r = await fetch(`${API}/api/messages/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok) {
        setMessages(d.messages || []);
        setActiveConv(d.conversation);
      }
    } catch (_) {}
  }, [token]);

  const markRead = useCallback(async (convId) => {
    try {
      await fetch(`${API}/api/messages/conversations/${convId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, unread_count: 0 } : c
      ));
    } catch (_) {}
  }, [token]);

  // Initial load
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Auto-select: a deep-linked conversation (?conversation=<id>, e.g. from a
  // push notification tap) when present and valid, otherwise the first one.
  useEffect(() => {
    if (activeId || draft || !loaded) return;
    // No conversation with a message in it: the message box straight away.
    if (visible.length === 0) { setDraft(true); setMobileShowThread(true); return; }
    const wanted = Number(new URLSearchParams(window.location.search).get('conversation'));
    const match = wanted && visible.find(c => c.id === wanted);
    setActiveId(match ? wanted : visible[0].id);
    // On mobile, open straight into the thread when deep-linked (otherwise the
    // list view shows first and the tap feels like it didn't land on the message).
    if (match) setMobileShowThread(true);
  }, [conversations, activeId, draft, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load messages when active changes
  useEffect(() => {
    if (!activeId) return;
    fetchMessages(activeId);
    markRead(activeId);
  }, [activeId, fetchMessages, markRead]);

  // Scroll to bottom when messages load
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 30s polling
  useEffect(() => {
    pollRef.current = setInterval(() => {
      fetchConversations();
      if (activeId) fetchMessages(activeId);
    }, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchConversations, fetchMessages, activeId]);

  // The shared composer's Attach file: the same upload route as before; the base64 rides on the message POST.
  async function uploadAttachment(file) {
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`${API}/api/messages/upload-attachment`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || d.message || 'Upload failed.');
    return { data: d.data, mime: d.mime, name: d.name };
  }

  // Resolution turn on an Eva answer. Captured on CLICK only — never inferred
  // from anything the borrower typed.
  async function resolveEva(msgId, resolution) {
    if (resolvingId) return;
    setResolvingId(msgId);
    try {
      const r = await fetch(`${API}/api/messages/${msgId}/resolution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ resolution }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not save.');
      setResolutions(prev => ({ ...prev, [msgId]: { resolution: d.resolution, ticket_number: d.ticket_number } }));
    } catch (err) {
      alert(err.message);
    } finally {
      setResolvingId(null);
    }
  }

  // 🔑 THE SHARED COMPOSER (2026-11-02): { body_html, body, attachment } — body_html is sanitized on the server against
  // the one allowlist; `body` is the plain copy (and the attachment-only caption, as before).
  async function handleSend({ body_html, body: plainBody, attachment }) {
    if (!String(plainBody || '').trim() && !attachment) return false;
    if (!activeId && !draft) return false;
    setSending(true);
    try {
      const body = String(plainBody || '').trim() || (attachment ? `[Attachment: ${attachment.name}]` : '');
      // The first message makes the conversation (or reuses an empty one already on file).
      let convId = activeId;
      if (draft) {
        if (emptyConv) convId = emptyConv.id;
        else {
          const cr = await fetch(`${API}/api/messages/conversations`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ subject: 'Question about my application' }),
          });
          const cd = await cr.json();
          if (!cr.ok) throw new Error(cd.error || 'Could not send your message.');
          convId = cd.conversation.id;
        }
      }
      const r = await fetch(`${API}/api/messages/conversations/${convId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          body,
          ...(String(plainBody || '').trim() ? { body_html } : {}),
          attachment_data: attachment?.data,
          attachment_mime: attachment?.mime,
          attachment_name: attachment?.name,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to send.');
      // 🔑 A message with an attachment can be held for the virus check (202 UPLOAD_CHECKING — constants/uploadChecking):
      // it is sent once the check passes and appears then (the thread re-reads every 30 s). The approved line says so;
      // while the wording is off nothing extra is shown.
      if (isUploadChecking({ status: r.status, body: d })) { const m = heldUploadMessage(); if (m) alert(m); }
      if (draft) { setDraft(false); setActiveId(convId); }
      // Wait briefly then refetch so any auto-reply is included
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchMessages(convId);
      fetchConversations();
      return true;
    } catch (err) {
      alert(err.message);
      return false;
    } finally {
      setSending(false);
    }
  }

  function handleSelectConv(conv) {
    setDraft(false);
    setActiveId(conv.id);
    setMobileShowThread(true);
  }

  function handleNewCreated(conv) {
    setShowNewModal(false);
    setConversations(prev => [conv, ...prev]);
    setActiveId(conv.id);
    setMobileShowThread(true);
  }

  // has_attachment is the DB-stored boolean (true when attachment_data is
  // populated OR attachment_url is set for legacy rows). attachment_url
  // alone is the old /uploads/ path which 404s post-Render redeploy, so
  // we don't fall through to it.
  // The message box — pinned at the bottom of the conversation pane (and of the first-message box).
  // 🔑 THE ONE MESSAGE BOX (the shared composer, approved v3 2026-11-02): a full-width box at the bottom of the thread,
  // growing to about 6 lines; one row under it — Aa, Attach file (a paperclip icon on a phone), Send. Enter is a new
  // line; Ctrl/Cmd+Enter sends (on a phone, the Send button). 16px text, so iOS does not zoom the page on focus.
  const composer = (
                  <div className="bg-white border-t border-gray-200 px-4 py-3 flex-shrink-0" data-testid="messages-composer">
                    <RichComposer
                      key={draft ? 'draft' : (activeId || 'none')}
                      testId="borrower-composer"
                      onSend={handleSend}
                      uploadAttachment={uploadAttachment}
                      attachAccept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.txt,image/*"
                      attachHint=""
                      attachLook="icon-on-phone"
                      attachmentLabel={(attachment) => friendlyFileName(attachment.name)}
                      placeholder={typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 639px)').matches
                        ? 'Type a message…'
                        : `Type a message… (${/Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘' : 'Ctrl+'}↵ to send)`}
                      sendLabel="Send"
                      sendClassName="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors flex-shrink-0"
                      disabled={sending}
                      minLines={2}
                      maxLines={6}
                      fontSize={16}
                      formatBarKey="doorly_app_composer_format_bar"
                    />
                  </div>
  );

  const isAttachment = (msg) => (msg.has_attachment || (msg.attachment_data)) && msg.attachment_name;

  return (
    <Frame>
      {/* ⚠️ DERIVED FROM THE TOPBAR, NOT A COPY OF ITS ARITHMETIC. This was
          h-[calc(100vh-64px)] — wrong twice: the topbar is 3.5rem (56px), and
          the hardcoded number could not see env(safe-area-inset-top) at all, so
          on an inset device the column overhung the viewport and pushed the
          composer below the fold. --app-topbar-h is defined once in index.css
          and consumed by the bar, its flow spacer, and here. */}
      {/* 🔑 In the guided shell (bare) the page fills the space the shell gives it (GuidedShell sizes it to the viewport
          under its header): the list and the thread scroll on their own and the message box stays pinned. */}
      {/* 🔑 At /messages the page sits in the borrower page container (components/BorrowerPageContainer; Hannah's review
          2026-11-03): the title at the container's left edge like every other page, the conversations in a white card —
          no longer a full-width bar. Its height leaves the container's top (24px) and the shell's bottom (32px). */}
      <div data-testid="messages-page" className={bare ? 'flex flex-col bg-gray-50' : 'flex flex-col'} style={{ height: bare ? '100%' : 'calc(100vh - var(--app-topbar-h) - 56px)', minHeight: 0 }}>
        {/* Header */}
        <div className={bare ? 'bg-white border-b border-gray-200 px-6 py-4 flex flex-row items-center justify-between gap-3 flex-shrink-0' : 'pb-4 flex flex-row items-start justify-between gap-3 flex-shrink-0'} data-testid="messages-header">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Messages</h1>
          </div>
          <button
            data-testid="messages-new"
            onClick={() => {
              // Guided: a new message box (or the empty conversation already on file) — nothing is created until sent.
              if (bare) { setActiveId(null); setActiveConv(null); setMessages([]); setDraft(true); setMobileShowThread(true); return; }
              setShowNewModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            {/* 🔑 "+ New message" everywhere (approved wording, Hannah 2026-11-03; was "+ New Conversation" at /messages). */}
            + New message
          </button>
        </div>

        {/* Two-panel layout */}
        <div className={bare ? 'flex flex-1 overflow-hidden' : 'flex flex-1 overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-sm'}>
          {/* Conversation list */}
          <div className={`w-full md:w-[30%] md:max-w-xs bg-white border-r border-gray-200 flex flex-col overflow-hidden flex-shrink-0 ${mobileShowThread ? 'hidden md:flex' : 'flex'}`}>
            <div className="overflow-y-auto flex-1">
              {visible.length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm">No conversations yet.</div>
              ) : (
                visible.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => handleSelectConv(conv)}
                    className={`w-full text-left px-4 py-3.5 border-b border-gray-100 transition-colors ${
                      activeId === conv.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className={`text-sm font-semibold truncate ${conv.unread_count > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                        {conv.subject}
                      </span>
                      <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{fmtTime(conv.last_message_at)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1 gap-2">
                      <p className="text-xs text-gray-400 truncate flex-1">{conv.last_message_body || 'No messages yet'}</p>
                      {conv.unread_count > 0 && (
                        <span className="flex-shrink-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    {/* No open / closed pill for the borrower (Jesse, 2026-10-19). */}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Active conversation / thread */}
          <div className={`flex-1 flex flex-col overflow-hidden ${!mobileShowThread ? 'hidden md:flex' : 'flex'}`}>
            {draft ? (
              // 🔑 THE FIRST MESSAGE (Jesse, 2026-10-19). A staff reply emails the borrower (routes/messages.js →
              // sendBorrowerReplyNotification), so "and let you know by email" is true.
              <div data-testid="messages-draft" className="flex-1 flex flex-col min-h-0">
                <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 flex-shrink-0">
                  {visible.length > 0 && (
                    <button type="button" onClick={() => { setDraft(false); setMobileShowThread(false); if (visible[0]) setActiveId(visible[0].id); }}
                      className="md:hidden text-gray-500 hover:text-gray-700 mr-1" aria-label="Back to conversations">←</button>
                  )}
                  <div className="flex-1 min-w-0">
                    <h2 className="font-semibold text-gray-900">Message our team</h2>
                    <p className="text-xs text-gray-500">Ask anything about your application. We&rsquo;ll reply here and let you know by email.</p>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto" />
                {composer}
              </div>
            ) : activeConv ? (
              <>
                {/* Thread header */}
                <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 flex-shrink-0">
                  <button
                    onClick={() => setMobileShowThread(false)}
                    className="md:hidden text-gray-500 hover:text-gray-700 mr-1"
                    aria-label="Back to conversations"
                  >
                    ←
                  </button>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-semibold text-gray-900 truncate">{activeConv.subject}</h2>
                    <p className="text-xs text-gray-400">Started {fmtFull(activeConv.created_at)}</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  {messages.map(msg => {
                    // THREE-way, deliberately. `sender_role` is 'borrower' |
                    // 'support' | 'eva'. The old two-way form sent anything that
                    // was not 'borrower' down the staff path, which would have
                    // rendered an Eva reply inside the NMLS-signed staff bubble
                    // labelled "Doorly Team" — indistinguishable from a person.
                    const isBorrower = msg.sender_role === 'borrower';
                    const isEva      = msg.sender_role === 'eva';
                    // Backend JOIN supplies sender_first_name / last_name /
                    // sender_role_internal when available; fall back to the
                    // generic "Doorly Team" label for legacy rows that
                    // pre-date the per-staff attribution fix.
                    const senderName = msg.sender_first_name
                      ? `${msg.sender_first_name} ${msg.sender_last_name || ''}`.trim()
                      : 'Doorly Team';
                    const isREDeskSender = msg.sender_role_internal === 'contract_desk';
                    // 🔑 A loan-officer staff sender shows by name only (Jesse, 2026-10-18): the one accurate role label is
                    // the licensed title, and borrower copy never says "loan officer".
                    const roleSuffix     = isREDeskSender ? ' — Real Estate Desk' : '';
                    return (
                      <div key={msg.id} className={`flex ${isBorrower ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] ${isBorrower ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                          {isEva && (
                            // Eva's byline — same avatar-circle treatment as a
                            // team member, but her own name and an explicit
                            // "AI assistant" line, never a title or an NMLS.
                            <div className="ml-1"><EvaSignature /></div>
                          )}
                          {!isBorrower && !isEva && (
                            // Full staff signature (avatar + name + title + NMLS)
                            // so the borrower sees exactly who replied. Falls
                            // back to a plain name+role label for legacy rows
                            // with no sender_* identity fields.
                            (msg.sender_first_name || msg.sender_title || msg.sender_nmls)
                              ? <div className="ml-1"><MessageSignature sender={msg} /></div>
                              : <span className="text-xs text-gray-500 ml-1 font-medium">
                                  {senderName}
                                  {roleSuffix && <span className="text-gray-400 font-normal">{roleSuffix}</span>}
                                </span>
                          )}
                          <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                            isBorrower
                              ? 'bg-blue-600 text-white rounded-br-sm'
                              : isEva
                                ? 'bg-blue-50 border border-blue-100 text-gray-800 rounded-bl-sm'
                                : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'
                          }`}>
                            {/* 🔑 A formatted staff message (the composer, 2026-10-31): the server's sanitised HTML, then its
                                attachment. Every other message renders exactly as before. */}
                            {hasRichBody(msg) ? (
                              <>
                                <RichMessageBody msg={msg} dark={isBorrower} />
                                {isAttachment(msg) && (
                                  <div className="mt-2"><MessageAttachment msgId={msg.id} name={msg.attachment_name} token={token} dark={isBorrower} borrowerView /></div>
                                )}
                              </>
                            ) : isAttachment(msg) ? (
                              <MessageAttachment msgId={msg.id} name={msg.attachment_name} token={token} dark={isBorrower} borrowerView />
                            ) : (
                              <p className="whitespace-pre-wrap">{msg.body}</p>
                            )}
                          </div>
                          {isEva && (
                            <EvaResolution
                              resolution={resolutions[msg.id]?.resolution ?? msg.resolution ?? null}
                              ticketNumber={resolutions[msg.id]?.ticket_number ?? msg.ticket_number ?? null}
                              busy={resolvingId === msg.id}
                              onResolve={(r) => resolveEva(msg.id, r)}
                            />
                          )}
                          <span className="text-xs text-gray-400">{fmtFull(msg.created_at)}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                {/* Input area */}
                {activeConv.status === 'open' ? (
                  composer
                ) : (
                  <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 text-sm text-gray-500 text-center flex-shrink-0">
                    This conversation is closed.
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <div className="text-5xl mb-3">💬</div>
                  <p className="text-base font-medium text-gray-500">Select a conversation</p>
                  <p className="text-sm text-gray-400 mt-1">or start a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showNewModal && (
        <NewConvModal token={token} onCreated={handleNewCreated} onClose={() => setShowNewModal(false)} />
      )}
    </Frame>
  );
}
